import React, { useMemo } from 'react';
import { Box, Paper, Skeleton } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getDemographyChartPaperBarSx, getDemographySectionPaperSx } from '../../util/chartTheme';

export const KPI_SKELETON_CARD_COUNT = 12;

const CHART_SKELETON_BAR_HEIGHTS = [42, 68, 36, 88, 52, 74, 40, 82, 48, 70];

export function StatisticKpiSkeletonGrid() {
  const theme = useTheme();
  const skeletonSx = useMemo(() => {
    const isLight = theme.palette.mode === 'light';
    const barBg = isLight ? alpha(theme.palette.common.black, 0.12) : alpha(theme.palette.common.white, 0.09);
    const mutedBg = isLight ? alpha(theme.palette.common.black, 0.08) : alpha(theme.palette.common.white, 0.06);
    const cardBg = isLight ? theme.palette.grey[100] : alpha(theme.palette.common.white, 0.03);
    const cardBorder = isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.06);
    return { barBg, mutedBg, cardBg, cardBorder };
  }, [theme]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 2,
      }}
    >
      {Array.from({ length: KPI_SKELETON_CARD_COUNT }, (_, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{
            p: 1.75,
            borderRadius: 2,
            bgcolor: skeletonSx.cardBg,
            border: `1px solid ${skeletonSx.cardBorder}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          <Skeleton variant='text' width='62%' height={14} animation='wave' sx={{ bgcolor: skeletonSx.mutedBg }} />
          <Skeleton variant='rounded' height={36} width='44%' animation='wave' sx={{ bgcolor: skeletonSx.barBg }} />
        </Paper>
      ))}
    </Box>
  );
}

export function StatisticChartSkeleton() {
  const theme = useTheme();
  const skeletonSx = useMemo(() => {
    const isLight = theme.palette.mode === 'light';
    const barBg = isLight ? alpha(theme.palette.common.black, 0.12) : alpha(theme.palette.common.white, 0.09);
    const mutedBg = isLight ? alpha(theme.palette.common.black, 0.08) : alpha(theme.palette.common.white, 0.06);
    const dividerColor = isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.06);
    return { barBg, mutedBg, dividerColor };
  }, [theme]);

  const sectionPaperSx = useMemo(() => getDemographySectionPaperSx(theme), [theme]);
  const chartPaperBarSx = useMemo(() => getDemographyChartPaperBarSx(theme), [theme]);

  return (
    <Box sx={{ mt: 3 }}>
      <Skeleton variant='text' width={260} height={28} animation='wave' sx={{ mb: 0.75, bgcolor: skeletonSx.mutedBg }} />
      <Skeleton variant='text' width='42%' height={16} animation='wave' sx={{ mb: 2, bgcolor: skeletonSx.mutedBg }} />
      <Paper
        elevation={0}
        sx={{
          ...sectionPaperSx,
          ...chartPaperBarSx,
          minHeight: 360,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: { xs: 0.75, sm: 1.25 },
            minHeight: 260,
            px: { xs: 1, sm: 2 },
            pt: 2,
            pb: 1,
          }}
        >
          {CHART_SKELETON_BAR_HEIGHTS.map((h, i) => (
            <Skeleton
              key={i}
              variant='rounded'
              animation='wave'
              sx={{
                width: { xs: 22, sm: 28 },
                height: h,
                bgcolor: skeletonSx.barBg,
                borderRadius: 1,
              }}
            />
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            py: 1.5,
            borderTop: `1px solid ${skeletonSx.dividerColor}`,
          }}
        >
          <Skeleton variant='text' width={72} height={12} sx={{ bgcolor: skeletonSx.mutedBg }} />
          <Skeleton variant='text' width={72} height={12} sx={{ bgcolor: skeletonSx.mutedBg }} />
        </Box>
      </Paper>
    </Box>
  );
}

export function StatisticPageLoadingSkeleton() {
  return (
    <>
      <StatisticKpiSkeletonGrid />
      <StatisticChartSkeleton />
    </>
  );
}
